import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CheckCircle2, ShieldCheck } from 'lucide-react';
import { Button } from '../components';
import api from '../services/api';
import type { Deel5Weergave } from '../services/gespreksverslagApi';

/**
 * Publiek: /verslag-akkoord/:token — de medewerker zonder portaalaccount geeft
 * toestemming om een rapportage met HR te delen (two-gate, migratie 090).
 * Bij een engine-verslag (migratie 176) toont de link precies Deel 5.
 */
interface ConsentData {
  title: string;
  verslag_datum: string;
  content: string;
  professional_name: string;
  deel5: Deel5Weergave | null;
}

const VerslagAkkoordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [klaar, setKlaar] = useState(false);
  const { data, error, isLoading } = useQuery({
    queryKey: ['verslag-consent', token],
    queryFn: () => api.get(`/verslagen/consent/${token}`).then((r) => r.data as { already_given?: boolean; data?: ConsentData; message?: string }),
    retry: false,
  });
  const akkoord = useMutation({
    mutationFn: () => api.post(`/verslagen/consent/${token}`),
    onSuccess: () => setKlaar(true),
  });

  const kader = (inhoud: React.ReactNode) => (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="max-w-xl mx-auto bg-white rounded-xl border border-gray-200 p-6 space-y-4">{inhoud}</div>
    </div>
  );

  if (isLoading) return kader(<p className="text-sm text-gray-500">Laden…</p>);
  if (error) return kader(<p className="text-sm text-gray-700">{(error as any)?.response?.data?.message || 'Deze link is ongeldig.'}</p>);
  if (klaar || data?.already_given) {
    return kader(
      <p className="flex items-center gap-2 text-green-700">
        <CheckCircle2 className="w-5 h-5" aria-hidden />
        Bedankt, uw toestemming is geregistreerd.
      </p>
    );
  }
  const v = data?.data;
  if (!v) return kader(<p className="text-sm text-gray-700">Deze link is ongeldig.</p>);

  return kader(
    <>
      <h1 className="text-xl font-semibold text-gray-900">Toestemming voor delen met HR</h1>
      <p className="text-sm text-gray-600">
        {v.professional_name} vraagt of onderstaande informatie uit het gesprek van {new Date(v.verslag_datum).toLocaleDateString('nl-NL')} met HR of uw
        leidinggevende gedeeld mag worden. Zonder uw toestemming ziet de werkgever niets.
      </p>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm space-y-2">
        {v.deel5 ? (
          <>
            <p><span className="font-medium">Arbo-kader:</span> {v.deel5.arbo_kader}</p>
            {v.deel5.fte_belastbaarheid != null && (
              <p><span className="font-medium">Belastbaarheid:</span> {v.deel5.fte_belastbaarheid}% · <span className="font-medium">Verwachte verzuimduur:</span> {v.deel5.verwachte_verzuimreductie}</p>
            )}
            <p className="whitespace-pre-wrap">{v.deel5.werkvloer_advies}</p>
          </>
        ) : (
          <p className="whitespace-pre-wrap">{v.content}</p>
        )}
      </div>
      <p className="flex gap-1.5 text-xs text-gray-500">
        <ShieldCheck className="w-4 h-4 shrink-0" aria-hidden />
        Persoonlijke notities en de inhoud van het gesprek worden nooit gedeeld.
      </p>
      <Button loading={akkoord.isPending} onClick={() => akkoord.mutate()}>Ik geef toestemming</Button>
    </>
  );
};

export default VerslagAkkoordPage;
